import { useState, useEffect, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { fileUrl } from "@/lib/jobOptions";
import { PROVINCIE_LIJST } from "@/lib/jobOptions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, Pencil, Eye, MousePointerClick, ImageOff } from "lucide-react";
import { toast } from "sonner";

const SLOTS = [
  { v: "dashboard_top", l: "Dashboard (bovenaan)" },
  { v: "sidebar", l: "Zijbalk" },
  { v: "offerteflow", l: "Offerteflow" },
];
const ROLLEN = [
  { v: "monteur", l: "Professionals" },
  { v: "bedrijf", l: "Bedrijven" },
  { v: "particulier", l: "Particulieren" },
];

const EMPTY = {
  titel: "", afbeelding_id: "", doel_url: "",
  slots: ["dashboard_top"], rollen: [], provincies: [],
  start_datum: "", eind_datum: "", rotatie_seconden: 8, toon_publiek: false, actief: true,
};

function Check({ checked, onChange, label, testid }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
      <input type="checkbox" data-testid={testid} checked={checked} onChange={onChange}
        className="w-4 h-4 accent-orange-500" />
      <span>{label}</span>
    </label>
  );
}

export default function SponsorsTab() {
  const [banners, setBanners] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/banners"); setBanners(data.banners); }
    catch (e) { toast.error("Banners laden mislukt"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleArr = (key, val) =>
    setForm((f) => ({ ...f, [key]: f[key].includes(val) ? f[key].filter((x) => x !== val) : [...f[key], val] }));

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (b) => {
    setForm({
      titel: b.titel || "", afbeelding_id: b.afbeelding_id || "", doel_url: b.doel_url || "",
      slots: b.slots || [], rollen: b.rollen || [], provincies: b.provincies || [],
      start_datum: b.start_datum || "", eind_datum: b.eind_datum || "",
      rotatie_seconden: b.rotatie_seconden || 8, toon_publiek: !!b.toon_publiek, actief: b.actief !== false,
    });
    setEditId(b.id); setOpen(true);
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/banners/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, afbeelding_id: data.id }));
      toast.success("Afbeelding geüpload");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload mislukt"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.titel.trim()) return toast.error("Geef een titel in");
    if (!form.afbeelding_id) return toast.error("Upload eerst een afbeelding");
    if (!form.doel_url.trim()) return toast.error("Geef een doel-URL in");
    if (form.slots.length === 0) return toast.error("Kies minstens één plaatsing");
    setSaving(true);
    try {
      if (editId) await api.put(`/admin/banners/${editId}`, form);
      else await api.post("/admin/banners", form);
      toast.success(editId ? "Banner bijgewerkt" : "Banner aangemaakt");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Opslaan mislukt"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/admin/banners/${id}`); toast.success("Banner verwijderd"); load(); }
    catch (e) { toast.error("Verwijderen mislukt"); }
  };

  return (
    <div className="space-y-5" data-testid="sponsors-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">Beheer sponsorbanners met targeting op rol en provincie. Vertoningen en kliks worden bijgehouden voor je sponsorrapport.</p>
        <Button data-testid="new-banner-btn" onClick={openNew} className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm font-bold shrink-0">
          <Plus className="w-4 h-4 mr-1" /> Nieuwe banner
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-white">
              <TableHead>Banner</TableHead>
              <TableHead>Plaatsing</TableHead>
              <TableHead>Doelgroep</TableHead>
              <TableHead>Periode</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Vertoningen</TableHead>
              <TableHead className="text-right">Kliks</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {banners.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-400">Nog geen banners. Voeg je eerste sponsor toe.</TableCell></TableRow>
            )}
            {banners.map((b) => (
              <TableRow key={b.id} data-testid="banner-row">
                <TableCell>
                  <div className="flex items-center gap-3">
                    {b.afbeelding_id
                      ? <img src={fileUrl(b.afbeelding_id)} alt={b.titel} className="w-20 h-12 object-cover rounded-sm border border-slate-200" />
                      : <div className="w-20 h-12 bg-slate-100 rounded-sm flex items-center justify-center"><ImageOff className="w-4 h-4 text-slate-400" /></div>}
                    <span className="font-semibold text-slate-800">{b.titel}</span>
                  </div>
                </TableCell>
                <TableCell className="text-xs text-slate-600">{(b.slots || []).map((s) => SLOTS.find((x) => x.v === s)?.l || s).join(", ")}</TableCell>
                <TableCell className="text-xs text-slate-600">
                  {(b.rollen || []).length ? b.rollen.join(", ") : "Alle rollen"}
                  <br />
                  <span className="text-slate-400">{(b.provincies || []).length ? `${b.provincies.length} provincie(s)` : "Alle provincies"}</span>
                </TableCell>
                <TableCell className="text-xs text-slate-500">{b.start_datum || "—"} → {b.eind_datum || "∞"}</TableCell>
                <TableCell>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-sm ${b.actief ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                    {b.actief ? "Actief" : "Uit"}
                  </span>
                </TableCell>
                <TableCell className="text-right font-semibold"><Eye className="w-3 h-3 inline mr-1 text-slate-400" />{b.impressies || 0}</TableCell>
                <TableCell className="text-right font-semibold"><MousePointerClick className="w-3 h-3 inline mr-1 text-slate-400" />{b.clicks || 0}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1 justify-end">
                    <Button data-testid="edit-banner-btn" onClick={() => openEdit(b)} variant="ghost" size="sm" className="text-slate-500 hover:text-slate-900"><Pencil className="w-4 h-4" /></Button>
                    <Button data-testid="delete-banner-btn" onClick={() => remove(b.id)} variant="ghost" size="sm" className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Banner bewerken" : "Nieuwe banner"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-bold">Titel (sponsor)</Label>
              <Input data-testid="banner-titel-input" value={form.titel} onChange={(e) => setForm({ ...form, titel: e.target.value })} className="rounded-sm border-slate-300" placeholder="bv. Deceuninck profielen" />
            </div>
            <div>
              <Label className="text-xs font-bold">Afbeelding</Label>
              {form.afbeelding_id && <img src={fileUrl(form.afbeelding_id)} alt="preview" className="w-full rounded-sm border border-slate-200 mb-2" />}
              <Input data-testid="banner-image-input" type="file" accept="image/*" onChange={uploadImage} disabled={uploading} className="rounded-sm border-slate-300" />
              <p className="text-[11px] text-slate-400 mt-1">Aanbevolen: 728×90 (dashboard/offerte) of 300×250 (zijbalk). Max 10 MB.</p>
            </div>
            <div>
              <Label className="text-xs font-bold">Doel-URL</Label>
              <Input data-testid="banner-url-input" value={form.doel_url} onChange={(e) => setForm({ ...form, doel_url: e.target.value })} className="rounded-sm border-slate-300" placeholder="https://sponsor.be" />
            </div>
            <div>
              <Label className="text-xs font-bold mb-1.5 block">Plaatsingen</Label>
              <div className="flex flex-wrap gap-x-5 gap-y-2">
                {SLOTS.map((s) => <Check key={s.v} testid={`slot-${s.v}`} checked={form.slots.includes(s.v)} onChange={() => toggleArr("slots", s.v)} label={s.l} />)}
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold mb-1.5 block">Doelgroep (rollen) — leeg = alle</Label>
              <div className="flex flex-wrap gap-x-5 gap-y-2">
                {ROLLEN.map((r) => <Check key={r.v} testid={`rol-${r.v}`} checked={form.rollen.includes(r.v)} onChange={() => toggleArr("rollen", r.v)} label={r.l} />)}
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold mb-1.5 block">Provincies — leeg = alle (nationaal)</Label>
              <div className="grid grid-cols-2 gap-x-5 gap-y-2 max-h-40 overflow-y-auto border border-slate-200 rounded-sm p-3">
                {PROVINCIE_LIJST.map((p) => <Check key={p} testid={`prov-${p}`} checked={form.provincies.includes(p)} onChange={() => toggleArr("provincies", p)} label={p} />)}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold">Startdatum</Label>
                <Input data-testid="banner-start-input" type="date" value={form.start_datum} onChange={(e) => setForm({ ...form, start_datum: e.target.value })} className="rounded-sm border-slate-300" />
              </div>
              <div>
                <Label className="text-xs font-bold">Einddatum</Label>
                <Input data-testid="banner-end-input" type="date" value={form.eind_datum} onChange={(e) => setForm({ ...form, eind_datum: e.target.value })} className="rounded-sm border-slate-300" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold">Wisseltijd in de carrousel (seconden)</Label>
              <Input data-testid="banner-rotatie-input" type="number" min="2" max="60"
                value={form.rotatie_seconden}
                onChange={(e) => setForm({ ...form, rotatie_seconden: parseInt(e.target.value || "8", 10) })}
                className="rounded-sm border-slate-300" placeholder="8" />
              <p className="text-[11px] text-slate-400 mt-1">Hoelang deze banner zichtbaar blijft voordat de carrousel naar de volgende sponsor wisselt (min. 2s). Geldt per plaatsing met meerdere banners.</p>
            </div>
            <div className="flex items-center gap-3">
              <Switch data-testid="banner-toon-publiek-switch" checked={form.toon_publiek} onCheckedChange={(c) => setForm({ ...form, toon_publiek: c })} />
              <Label className="text-sm">Ook tonen op publieke pagina's (los van rol)</Label>
            </div>
            <div className="flex items-center gap-3">
              <Switch data-testid="banner-active-switch" checked={form.actief} onCheckedChange={(c) => setForm({ ...form, actief: c })} />
              <Label className="text-sm">Banner actief</Label>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="save-banner-btn" onClick={save} disabled={saving || uploading} className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm font-bold">
              {saving ? "Bezig..." : editId ? "Wijzigingen opslaan" : "Banner aanmaken"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
